import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Tooltip } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { NumericFormat } from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'

import type { LimitOrderModel } from '@root/entities/limit-order'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  order: LimitOrderModel
} & ClassNameProps

export const OrdersTableTriggerCondition: React.FC<Props> = ({
  className,
  order,
}) => {
  const { t } = useTranslation()

  const [valueHigh] = useAtom(order.triggerValueHigh)
  const [valueLow] = useAtom(order.triggerValueLow)

  if (order.type === 'buy') {
    const tooltipContent = t('order.limit-buy-tooltip', {
      price: NumericFormat.formatPreset((valueLow || valueHigh) ?? 0, 'full'),
      target:
        order.triggerType === 'absolute'
          ? t('price').toLowerCase()
          : t('market-cap').toLowerCase(),
      currency: '$',
    })

    return (
      <Tooltip content={tooltipContent}>
        <Root className={className}>
          <span>
            {order.triggerType === 'absolute'
              ? t('price')
              : t('trade.market-cap-short')}
          </span>
          <span> = </span>
          <Numeric
            currency="$"
            value={valueHigh || valueLow || '0'}
            preset="currency-amount-short"
          />
        </Root>
      </Tooltip>
    )
  }

  const renderStopLoss = () => {
    return (
      <div>
        <span>{t('trade.stop-loss-short')}: </span>
        <span>
          {order.triggerType === 'absolute'
            ? t('price')
            : t('trade.market-cap-short')}
        </span>
        <span>{' < '}</span>
        <Numeric
          currency="$"
          value={valueLow ?? 0}
          preset="currency-amount-short"
        />
      </div>
    )
  }

  const renderTakeProfit = () => {
    return (
      <div>
        <span>{t('trade.take-profit-short')}: </span>
        <span>
          {order.triggerType === 'absolute'
            ? t('price')
            : t('trade.market-cap-short')}
        </span>
        <span>{' > '}</span>
        <Numeric
          currency="$"
          value={valueHigh ?? 0}
          preset="currency-amount-short"
        />
      </div>
    )
  }

  const tooltipContent = t('order.limit-sell-tooltip', {
    stopLoss: NumericFormat.formatPreset(valueLow || '0', 'full'),
    takeProfit: NumericFormat.formatPreset(valueHigh || '0', 'full'),
    target: (order.triggerType === 'absolute'
      ? t('price')
      : t('market-cap')
    ).toLowerCase(),
    currency: '$',
  })

  return (
    <Tooltip content={tooltipContent}>
      <Root className={className}>
        {renderStopLoss()}
        {renderTakeProfit()}
      </Root>
    </Tooltip>
  )
}

const Root = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;

  color: var(--text-11);
`
